// v2.0.0 - 2026-01-31 - Member Earnings page with trend chart and history

import { useState, useEffect, useCallback } from 'react'
import { member } from '@/lib/api/client'
import { PageContainer, PageHeader } from '@/components/layout/PageContainer'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

interface EarningsSummary {
  total: number
  pending: number
  lastPayment: { amount: number; date: string } | null
}

interface TrendEntry {
  period: string
  netto: number
}

interface EarningsEntry {
  period: string
  tiQuota: number
  eccedenza: number
  netto: number
  status: 'approved' | 'distributed'
  immessa: number
  prelevata: number
  eaci: number
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value)
}

const formatNumber = (value: number, decimals = 2): string => {
  return new Intl.NumberFormat('it-IT', { maximumFractionDigits: decimals }).format(value)
}

const formatPeriod = (period: string): string => {
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
  const [year, month] = period.split('-')
  return `${months[parseInt(month) - 1]} ${year}`
}

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export default function MemberEarnings() {
  const [summary, setSummary] = useState<EarningsSummary | null>(null)
  const [trend, setTrend] = useState<TrendEntry[]>([])
  const [entries, setEntries] = useState<EarningsEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchEarnings = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const data = await member.earnings()
      setSummary(data.summary)
      setTrend(data.trend)
      setEntries(data.entries)
    } catch (e) {
      setError((e as Error).message || 'Errore nel caricamento guadagni')
    }

    setIsLoading(false)
  }, [])

  useEffect(() => {
    fetchEarnings()
  }, [fetchEarnings])

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader
          title="Guadagni"
          description="Storico quote TI"
        />
        <Card>
          <CardContent className="py-12 text-center text-[var(--color-error)]">
            {error}
          </CardContent>
        </Card>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="Guadagni"
        description="Storico quote Tariffa Incentivante"
      />

      {/* Stat Cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-[var(--color-text-secondary)]">Totale Guadagni</div>
            <div className="mt-2 text-2xl font-bold text-[var(--color-success)]">
              {formatCurrency(summary?.total || 0)}
            </div>
            <div className="mt-1 text-xs text-[var(--color-text-tertiary)]">
              Totale distribuito
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-[var(--color-text-secondary)]">Da Pagare</div>
            <div className="mt-2 text-2xl font-bold text-[var(--color-warning)]">
              {formatCurrency(summary?.pending || 0)}
            </div>
            <div className="mt-1 text-xs text-[var(--color-text-tertiary)]">
              Approvati, in attesa
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-[var(--color-text-secondary)]">Ultimo Pagamento</div>
            <div className="mt-2 text-2xl font-bold">
              {summary?.lastPayment ? formatCurrency(summary.lastPayment.amount) : '-'}
            </div>
            <div className="mt-1 text-xs text-[var(--color-text-tertiary)]">
              {summary?.lastPayment ? formatDate(summary.lastPayment.date) : 'Nessun pagamento'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      {trend.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Trend Guadagni</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend.map(t => ({ ...t, periodLabel: formatPeriod(t.period) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis
                    dataKey="periodLabel"
                    tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                    tickFormatter={(value) => `${value}€`}
                  />
                  <Tooltip
                    formatter={(value: number) => [formatCurrency(value), 'Netto']}
                    contentStyle={{
                      backgroundColor: 'var(--color-bg-primary)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="netto"
                    stroke="var(--color-success)"
                    strokeWidth={2}
                    dot={{ fill: 'var(--color-success)', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Earnings History Table */}
      <Card>
        <CardHeader>
          <CardTitle>Storico Quote</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="py-12 text-center text-[var(--color-text-secondary)]">
              Nessuna quota disponibile
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      Periodo
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-[var(--color-text-secondary)]">
                      Immessa (kWh)
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-[var(--color-text-secondary)]">
                      Prelevata (kWh)
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-[var(--color-text-secondary)]">
                      EACI (kWh)
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-[var(--color-text-secondary)]">
                      Quota TI
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-[var(--color-text-secondary)]">
                      Eccedenza
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-[var(--color-text-secondary)]">
                      Netto
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr
                      key={entry.period}
                      className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)]/50"
                    >
                      <td className="px-4 py-3 font-medium">
                        {formatPeriod(entry.period)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {formatNumber(entry.immessa)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {formatNumber(entry.prelevata)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {formatNumber(entry.eaci)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {formatCurrency(entry.tiQuota)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {formatCurrency(entry.eccedenza)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm font-bold text-[var(--color-success)]">
                        {formatCurrency(entry.netto)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={entry.status === 'distributed' ? 'success' : 'warning'}>
                          {entry.status === 'distributed' ? 'Distribuito' : 'Approvato'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
